import * as React from 'react';
import { Component } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import './SelectAllCheckbox.css';
import { getFrom, getTo } from '../../helpers/table/table';

interface Props {
  onSelectAllClick: () => void;
  onSelectAllOnPageClick: () => void;
  onUnselectAllClick: () => void;
  isChecked: boolean;
  totalCount: number;
  currentPage: number;
  selectedItems: any[];
  selectedItemsCount?: number;
  pageItemsCount?: number;
  isSelectDisabled?: boolean;
  isNoVisibleCheckbox?: boolean;
  compact?: boolean;
}

interface State {
  isChecked: boolean;
  isMenuOpen: boolean;
  menuLeft: number;
  menuTop: number;
}

export class SelectAllCheckbox extends Component<Props, State> {
  private readonly checkboxId = `selectAllCheckbox_${Math.random()
    .toString(36)
    .slice(2)}`;

  private readonly containerRef = React.createRef<HTMLDivElement>();

  constructor(props: Props) {
    super(props);

    this.state = {
      isChecked: props.isChecked,
      isMenuOpen: false,
      menuLeft: 0,
      menuTop: 0
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.isChecked !== this.props.isChecked) {
      this.setState({
        isChecked: nextProps.isChecked
      });
    }
  }

  componentWillUnmount() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('click', this.closeMenu);
      window.removeEventListener('resize', this.closeMenu);
      window.removeEventListener('scroll', this.closeMenu, true);
    }
  }

  onCheckedChange = (event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const { isChecked, isMenuOpen } = this.state;

    const { isSelectDisabled } = this.props;
    if (isSelectDisabled) {
      return;
    }

    if (isMenuOpen) {
      this.closeMenu();
      return;
    }

    const anchor = this.containerRef.current && this.containerRef.current.getBoundingClientRect();
    const menuWidth = 208;
    const menuLeft = anchor
      ? Math.max(8, Math.min(anchor.left, window.innerWidth - menuWidth - 8))
      : 8;
    const menuHeight = 132;
    const menuTop = anchor
      ? anchor.bottom + menuHeight + 8 > window.innerHeight
        ? Math.max(8, anchor.top - menuHeight - 6)
        : anchor.bottom + 6
      : 8;

    this.setState(
      {
        isMenuOpen: true,
        isChecked,
        menuLeft,
        menuTop
      },
      () => {
        setTimeout(() => {
          document.addEventListener('click', this.closeMenu);
          window.addEventListener('resize', this.closeMenu);
          window.addEventListener('scroll', this.closeMenu, true);
        }, 0);
      }
    );
  };

  closeMenu = () => {
    this.setState(
      {
        isMenuOpen: false,
        isChecked: this.props.isChecked
      },
      () => {
        document.removeEventListener('click', this.closeMenu);
        window.removeEventListener('resize', this.closeMenu);
        window.removeEventListener('scroll', this.closeMenu, true);
      }
    );
  };

  onSelectAllOnPageClick = event => {
    event.preventDefault();

    const { isSelectDisabled } = this.props;
    if (isSelectDisabled) {
      return;
    }

    this.props.onSelectAllOnPageClick();
    this.setState({ isMenuOpen: false });
    document.removeEventListener('click', this.closeMenu);
    window.removeEventListener('resize', this.closeMenu);
    window.removeEventListener('scroll', this.closeMenu, true);
  };

  onSelectAllClick = event => {
    event.preventDefault();

    const { isSelectDisabled } = this.props;
    if (isSelectDisabled) {
      return;
    }

    this.props.onSelectAllClick();
    this.setState({ isMenuOpen: false });
    document.removeEventListener('click', this.closeMenu);
    window.removeEventListener('resize', this.closeMenu);
    window.removeEventListener('scroll', this.closeMenu, true);
  };

  onUnselectAllClick = event => {
    event.preventDefault();

    if (this.props.isSelectDisabled) {
      return;
    }

    this.props.onUnselectAllClick();
    this.setState({ isMenuOpen: false, isChecked: false });
    document.removeEventListener('click', this.closeMenu);
    window.removeEventListener('resize', this.closeMenu);
    window.removeEventListener('scroll', this.closeMenu, true);
  };

  renderDropdownList() {
    const { isMenuOpen, menuLeft, menuTop } = this.state;
    const { totalCount, currentPage, isNoVisibleCheckbox, pageItemsCount, selectedItems, selectedItemsCount } = this.props;
    const selectedCount = typeof selectedItemsCount === 'number' ? selectedItemsCount : (selectedItems || []).length;

    const pageCount =
      typeof pageItemsCount === 'number'
        ? pageItemsCount
        : totalCount === 0
        ? 0
        : getTo(totalCount, currentPage) - getFrom(totalCount, currentPage) + 1;

    const listClassNames = classNames({
      'dropdown-menu': true,
      show: isMenuOpen,
      eSelectAllCheckbox: true
    });

    if (!isMenuOpen || typeof document === 'undefined') {
      return null;
    }

    return createPortal(
      <div
        className={listClassNames}
        style={{
          left: menuLeft,
          position: 'fixed',
          top: menuTop,
          zIndex: 10050
        }}
      >
        {!isNoVisibleCheckbox && (
          <a className="dropdown-item" onClick={this.onSelectAllOnPageClick}>
            {`Select this page (${pageCount})`}
          </a>
        )}
        <a className="dropdown-item" onClick={this.onSelectAllClick}>
          {`Select all (${totalCount})`}
        </a>
        <a
          className={classNames('dropdown-item is-clear-selection', { 'is-disabled': selectedCount === 0 })}
          aria-disabled={selectedCount === 0}
          onClick={selectedCount > 0 ? this.onUnselectAllClick : undefined}
        >
          {`Clear selection (${selectedCount})`}
        </a>
      </div>,
      document.body
    );
  }

  render() {
    const { selectedItems, selectedItemsCount, compact } = this.props;

    const selectedCount = typeof selectedItemsCount === 'number' ? selectedItemsCount : (selectedItems || []).length;

    return (
      <div
        ref={this.containerRef}
        className={classNames('d-inline form-check pl-0 selectAllCheckbox', {
          'mr-3': !compact,
          'is-compact': compact
        })}
      >
        <input
          type="checkbox"
          checked={this.state.isChecked}
          onChange={() => {}}
          onClick={this.onCheckedChange}
          id={this.checkboxId}
          className="mr-2"
          aria-label="Open selection options"
          aria-expanded={this.state.isMenuOpen}
          aria-haspopup="menu"
        />
        {!compact && (
          <label className="form-check-label" htmlFor={this.checkboxId}>
            {`Selected: ${selectedCount}`}
          </label>
        )}
        {this.renderDropdownList()}
      </div>
    );
  }
}
